/**
 * PURPOSE: Traducir los errores del módulo de Resultados de Atletismo a algo accionable.
 * RESPONSIBILITIES: Leer el sobre V2 (`{ ok:false, code, message, details }`) del error de axios y
 *   devolver `{ code, status, message, details, isConflict, isLocked }`. Puro y nunca lanza.
 *
 *   EL MENSAJE DEL BACKEND GANA cuando existe (mismo criterio que programmingCenterErrors.js): está
 *   escrito para el operador. El diccionario solo cubre el caso en que el servidor no manda texto.
 *
 *   `REVISION_CONFLICT` NUNCA SE REINTENTA SOLO: otro usuario guardó la prueba; se avisa y se ofrece
 *   recargar. Reenviar el mismo cuerpo pisaría cambios que nadie revisó.
 * COLLABORATORS: services/athleticsResultsService.js, area/AtletismoResultadosArea.
 * ROUTE: winscore/src/utils/athleticsResultsErrors.js
 */

export const ATHLETICS_ERROR_MESSAGES = Object.freeze({
  FEATURE_DISABLED: "El módulo de resultados de atletismo no está habilitado.",
  INVALID_ID: "El identificador de la solicitud no es válido.",
  INVALID_PAYLOAD: "Los datos enviados no son válidos.",
  INVALID_MARK: "Hay una marca con formato inválido. Revisa la celda resaltada.",
  INVALID_WIND: "El viento no tiene un formato válido (ej.: +1.2, -0.5, 0.0).",
  INVALID_REVISION: "Falta la revisión de la prueba. Recarga e inténtalo de nuevo.",
  SESSION_REQUIRED: "Tu sesión expiró. Vuelve a iniciar sesión para continuar.",
  USER_NOT_ENABLED: "Tu usuario no está habilitado.",
  ROLE_NOT_ALLOWED: "Tu rol no permite esta acción.",
  EVENT_ACCESS_DENIED: "No tienes acceso a este evento.",
  COMPANY_NOT_ENABLED: "Tu compañía no tiene habilitado este módulo.",
  EVENT_NOT_FOUND: "El evento no existe.",
  PRUEBA_NOT_FOUND: "La prueba no existe.",
  ROUND_NOT_FOUND: "La serie no existe.",
  ENTRY_NOT_FOUND: "El inscrito ya no está en esta serie.",
  REVISION_CONFLICT: "Otro usuario guardó cambios en esta prueba.",
  PRUEBA_OFFICIAL_LOCKED: "La prueba es oficial y ya no se puede editar.",
  FINAL_HAS_RESULTS: "La final ya tiene marcas: no se puede volver a armar.",
  NO_HEAT_RESULTS: "Las series todavía no tienen tiempos para armar la final.",
  ENTRY_ALREADY_IN_ROUND: "Ese inscrito ya está en la serie.",
  LANE_TAKEN: "Ese carril ya está ocupado en la serie.",
  RESULTS_IN_PROGRESS: "Hay resultados en curso que impiden esta acción.",
  SCHEMA_NOT_READY: "La base de datos todavía no está preparada para este módulo.",
  INTERNAL_ERROR: "Ocurrió un error interno en el servidor.",
});

const FALLBACK_BY_STATUS = {
  400: "La solicitud no es válida.",
  401: "Tu sesión expiró. Vuelve a iniciar sesión.",
  403: "No tienes permiso para esta acción.",
  404: "El recurso no existe o no está disponible.",
  409: "El estado de la prueba cambió mientras trabajabas. Recarga la vista.",
  503: "El servicio no está disponible en este momento.",
};

export const parseAthleticsError = (error) => {
  const response = error?.response;
  const body = response?.data && typeof response.data === "object" ? response.data : null;
  const status = Number(response?.status) || null;
  const code = typeof body?.code === "string" ? body.code : null;
  const details = body?.details && typeof body.details === "object" ? body.details : null;

  const message =
    (typeof body?.message === "string" && body.message.trim()) ||
    (code && ATHLETICS_ERROR_MESSAGES[code]) ||
    (status && FALLBACK_BY_STATUS[status]) ||
    (error?.message === "Network Error"
      ? "No se pudo conectar con el servidor."
      : "Ocurrió un error inesperado.");

  return {
    code,
    status,
    message,
    details,
    isConflict: code === "REVISION_CONFLICT",
    isLocked: code === "PRUEBA_OFFICIAL_LOCKED",
  };
};

/** Texto listo para un toast, con el contexto de la acción delante cuando se da. */
export const athleticsErrorMessage = (error, prefix = "") => {
  const { message } = parseAthleticsError(error);
  return prefix ? `${prefix}: ${message}` : message;
};
